/**
 * Реализация отображение списка инофрмации по клиентам которые запрашивали загрузку тм-апи продукта
 */
import React, { cloneElement, useMemo, useState } from 'react';

import {
    Datagrid,
    DateField,
    ExportButton,
    FilterButton,
    List,
    Show,
    SimpleShowLayout,
    TextInput,
    TextField,
    TopToolbar, 
    useRecordContext,
    NumberInput,
    RaRecord,
    Title} from 'react-admin';

import { Box, Typography } from '@mui/material';
import TextViewField from 'components/text-view';
import SnapShotTextField from 'components/snapshot-text-field';
import ButtonLink from 'components/buttons/link';
import Tablist from 'components/tabs/tab-list';
import StatisticsPanel from './statistics';
import {DateRange as DateRangeInfo} from 'components/date-range/types';
import DateRanges from 'components/date-range/ra-date-range';
import { observer } from 'mobx-react-lite';
import { capitalizeFirstLetter } from 'utilities/algorithm/string';
import { useStoreContext } from 'admin/store-context';
import { getResourcePath } from 'admin/pages-factory';

// Фильтрация элементов по колонкам
const getFilters = (permissions: string): React.ReactElement[] => {
  const retList: React.ReactElement[] = 
  [
    <TextInput source="dci" label = 'DCI' autoFocus/>,
    <DateRanges source='creation' label='Date'/>,
    <TextInput source="name" label = 'Name' autoFocus/>,
    <TextInput source="position" label = 'Position' autoFocus/>,
    <TextInput source="email" label = 'Email' type='email' autoFocus/>,
    <TextInput source="question" label = 'Comment' autoFocus/>,
    <TextInput source="version" label = 'Version' autoFocus/>,
    <TextInput source="os" label = 'OS' autoFocus/>,
    <NumberInput source="downloadCount" label = 'Download count' min={0} step={1}/>
  ];
  return retList;
}

type RaProps = Omit<RaRecord, 'id'>;

interface Props extends RaProps {
  permission: string;
}

const DetailPanel: React.FC<RaProps> = props => {
  const record = useRecordContext();
  return  (
    <SimpleShowLayout>
        <TextViewField source="question" label = 'User Comment'/>
        <ButtonLink label = 'Open Web Clients'
                    redirectTo = 'list'
                    pathTo = {getResourcePath('/clients')}
                    filter = {{id: record.dci}} />
    </SimpleShowLayout>
  );
}

const ShowDetail: React.FC<RaProps> = props => {
  return (
    <Show
      {...props}
      /* disable the app title change when shown */
      title=" ">
        <DetailPanel {...props} />
    </Show>);
}

const Empty = () => {
  return (
      <Box textAlign="center" m={1}>
          <Typography variant="h4" paragraph>
            No users who performed the download
          </Typography>
      </Box>
  );
};

const ListActions: React.FC<Props> = props => {

  return (
    <TopToolbar>
        {props.filters && cloneElement(props.filters, { context: 'button' })}
        <FilterButton/>
        <ExportButton/>
    </TopToolbar>
  );
}

const DownloadsList: React.FunctionComponent<Props> = ({ permissions, ...props }) => {
  return (
    <>
      <List
            {...props}
            empty={<Empty />}
            title= ' '
            filters={getFilters(permissions)}
            sort={{ field: 'creation', order: 'DESC' }}
            perPage={25}
            actions={<ListActions {...props}/>}
            bulkActionButtons={false}>
        <Datagrid 
          expand={<ShowDetail {...props}/>}
          bulkActionButtons={false}>
            <SnapShotTextField source="dci" label = 'DCI'/>
            <DateField source="creation" label = 'Date'/>
            <SnapShotTextField source="name" label = 'Name' maxPrevLen={40}/>
            <SnapShotTextField source="position" label = 'Position' maxPrevLen={40}/>
            <SnapShotTextField source="email" label = 'Email'/>
            <SnapShotTextField source="question" label='Comment' maxPrevLen={100}/>
            <SnapShotTextField source="version" label = 'Version' maxPrevLen={40}/>
            <SnapShotTextField source="os" label = 'OS' maxPrevLen={40}/>
            <TextField source="downloadCount" label = 'Download count' />
        </Datagrid>
      </List>
    </>
  );
}

const MainPanel: React.FC<Props> = props => {
  const {services} = useStoreContext();
  const [dateRange, setDateRange] = useState<DateRangeInfo | undefined>(undefined);
  const [active, setActiveTab] = React.useState<string>('Statistic');
  const label = useMemo(()=>`${capitalizeFirstLetter(services.activeId??'')}/Downloads`, [services.activeId]);
  const listTab = [
        {
          label: 'Statistic',
          render: <StatisticsPanel dateRange={dateRange} onDateChanged={setDateRange}/>
        },
        {
          label: 'Data',
          render: <DownloadsList {...props}/>
        },
    ];
  return ( 
      <Box>
        <Title title={label} />
        <Tablist list={listTab} active={active} onActive={setActiveTab}/>
      </Box> );
}

export default observer(MainPanel); 